import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Plus, Trash, User, Users } from "@phosphor-icons/react";
import { API, authHeaders } from "@/lib/api";
import { useLocale } from "@/i18n";

export default function TeamTab({ apiBase }) {
  const { t } = useLocale();
  const base = apiBase || `${API}/barber/professionals`;
  const [team, setTeam] = useState(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${base}`, { headers: authHeaders() });
      setTeam(res.data);
    } catch {
      toast.error(t("team_error"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) return toast.warning(t("team_name_ph"));
    setSaving(true);
    try {
      await axios.post(`${base}`, { name: name.trim() }, { headers: authHeaders() });
      toast.success(t("team_added"));
      setName("");
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("team_error"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (pid) => {
    try {
      await axios.delete(`${base}/${pid}`, { headers: authHeaders() });
      toast.success(t("team_deleted"));
      load();
    } catch {
      toast.error(t("team_error"));
    }
  };

  const uploadPhoto = async (pid, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingId(pid);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${base}/${pid}/photo`, fd, { headers: authHeaders() });
      toast.success(t("team_added"));
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("team_error"));
    } finally {
      setUploadingId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="team-tab">
      <div>
        <h2 className="text-lg font-bold text-zinc-100 flex items-center gap-2">
          <Users size={20} weight="duotone" className="text-amber-500" /> {t("team_title")}
        </h2>
        <p className="text-xs text-zinc-500 mt-1">{t("team_hint")}</p>
      </div>

      <form onSubmit={add} className="flex gap-2" data-testid="team-add-form">
        <input
          data-testid="team-name-input"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("team_name_ph")}
          className="flex-1 h-11 px-3 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition-colors text-sm"
        />
        <button
          data-testid="team-add-btn"
          type="submit"
          disabled={saving}
          className="h-11 px-4 rounded-lg bg-amber-500 text-zinc-950 font-bold flex items-center gap-1.5 hover:bg-amber-400 transition-colors active:scale-95 disabled:opacity-60"
        >
          <Plus size={16} weight="bold" /> {t("team_add")}
        </button>
      </form>

      <div className="space-y-2" data-testid="team-list">
        {team === null ? (
          <div className="flex justify-center py-6"><div className="h-6 w-6 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" /></div>
        ) : team.length === 0 ? (
          <p className="text-sm text-zinc-500 bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="team-empty-msg">{t("team_empty")}</p>
        ) : (
          team.map((p) => (
            <div key={p.id} className="flex items-center gap-3 bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3" data-testid={`team-row-${p.id}`}>
              {p.photo_url ? (
                <img src={p.photo_url} alt={p.name} className="w-11 h-11 rounded-full object-cover border-2 border-amber-500" />
              ) : (
                <div className="w-11 h-11 rounded-full border-2 border-zinc-700 bg-zinc-800 flex items-center justify-center shrink-0">
                  <User size={18} className="text-zinc-500" weight="duotone" />
                </div>
              )}
              <span className="flex-1 font-semibold text-zinc-100 truncate">{p.name}</span>
              <label className="cursor-pointer p-2 text-zinc-400 hover:text-amber-500 transition-colors" title={t("team_photo_btn")}>
                <input data-testid={`team-photo-input-${p.id}`} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(p.id, e)} />
                {uploadingId === p.id ? (
                  <div className="h-[18px] w-[18px] rounded-full border-2 border-amber-500 border-t-transparent animate-spin" />
                ) : (
                  <Camera size={18} weight="duotone" />
                )}
              </label>
              <button data-testid={`team-delete-btn-${p.id}`} onClick={() => remove(p.id)} className="p-2 text-zinc-500 hover:text-red-400 transition-colors">
                <Trash size={17} weight="bold" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
